import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { useAuth } from "@/contexts/AuthContext";
import { api, COUNTRY_FLAGS, COUNTRY_LABELS, formatApiError } from "@/lib/api";
import { Users, Home, Eye, MessageSquare, Heart, Rocket, TrendingUp, TrendingDown, Wallet, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const PERIODS = [7, 30, 90];

export default function AdminAnalytics() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (loading) return;
    if (!user) return navigate("/login");
    if (user.role !== "admin") return navigate("/dashboard");
    let cancelled = false;
    (async () => {
      setError(null);
      try {
        const { data } = await api.get("/admin/analytics", { params: { days } });
        if (!cancelled) setData(data);
      } catch (e) {
        if (cancelled) return;
        const msg = formatApiError(e);
        setError(msg);
        toast.error(msg);
      }
    })();
    return () => { cancelled = true; };
  }, [user, loading, days, navigate]);

  if (!user || user.role !== "admin") {
    return (
      <Layout withFooter={false}>
        <div className="max-w-6xl mx-auto px-6 py-12 text-muted-foreground">Chargement…</div>
      </Layout>
    );
  }

  if (!data) {
    return (
      <Layout withFooter={false}>
        <div className="max-w-6xl mx-auto px-6 lg:px-8 py-8" data-testid="admin-analytics-loading">
          <div className="h-10 w-72 rounded-md bg-[#F0EBE6] animate-pulse mb-8" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {[0,1,2,3].map(i => (<div key={i} className="h-32 rounded-2xl bg-[#F5F0EB] animate-pulse" />))}
          </div>
          <div className="grid md:grid-cols-2 gap-4 mb-8">
            {[0,1].map(i => (<div key={i} className="h-36 rounded-2xl bg-[#F5F0EB] animate-pulse" />))}
          </div>
          <div className="h-64 rounded-2xl bg-[#F5F0EB] animate-pulse mb-8" />
          {error && (
            <div data-testid="admin-analytics-error" className="rounded-2xl border border-[#D6202C]/30 bg-[#FEF2F3] text-[#D6202C] p-4 text-sm">
              Erreur de chargement : {error}
            </div>
          )}
        </div>
      </Layout>
    );
  }

  const growth = data.users.growth_pct;
  const growthColor = growth == null ? "text-muted-foreground" : growth >= 0 ? "text-[#11783A]" : "text-[#D6202C]";

  return (
    <Layout withFooter={false}>
      <div className="max-w-6xl mx-auto px-6 lg:px-8 py-8" data-testid="admin-analytics">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
          <div>
            <span className="text-xs tracking-[0.2em] uppercase text-[#11783A] font-medium">Admin</span>
            <h1 className="font-heading text-4xl md:text-5xl font-medium tracking-tight mt-2">Analytique marketplace</h1>
            <p className="text-muted-foreground mt-1">Vue d'ensemble des {data.period_days} derniers jours</p>
          </div>
          <div className="flex gap-1 bg-white border border-[#E5E5E5] rounded-full p-1">
            {PERIODS.map((d) => (
              <button
                key={d}
                onClick={() => setDays(d)}
                data-testid={`analytics-period-${d}`}
                className={`px-3 h-8 rounded-full text-xs font-medium transition ${
                  days === d ? "bg-[#1A1A1A] text-white" : "hover:bg-[#FAFAFA]"
                }`}
              >
                {d}j
              </button>
            ))}
          </div>
        </div>

        {/* Top KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <KpiCard
            icon={<Users />}
            label="Utilisateurs"
            value={data.users.total}
            sub={
              <span className={`text-xs ${growthColor} flex items-center gap-0.5`}>
                {growth != null && (growth >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />)}
                {data.users.new_period} nouveaux {growth != null ? `(${growth > 0 ? "+" : ""}${growth}%)` : ""}
              </span>
            }
          />
          <KpiCard icon={<Home />} label="Annonces actives" value={data.listings.total} sub={`${data.listings.new_period} nouvelles · ${data.listings.boosted_active} boostées`} />
          <KpiCard icon={<Eye />} label="Vues totales" value={data.engagement.total_views} sub={`${data.engagement.period_views} sur la période`} />
          <KpiCard icon={<Wallet />} label="Transactions" value={data.revenue.total_transactions_alltime} sub="payées" highlight />
        </div>

        {/* Revenue */}
        <Section title="Revenus" data-testid="analytics-revenue">
          <div className="grid md:grid-cols-2 gap-4">
            <RevenueCard label="Boost (paiements ponctuels)" data={data.revenue.by_kind_currency.boost} count={data.revenue.transactions_count.boost} />
            <RevenueCard label="Abonnements Pro" data={data.revenue.by_kind_currency.pro_subscription} count={data.revenue.transactions_count.pro_subscription} highlight />
          </div>
        </Section>

        {/* Users split */}
        <Section title="Utilisateurs" data-testid="analytics-users">
          <div className="grid grid-cols-2 gap-4">
            <SplitCard label="Particuliers" value={data.users.particulier} total={data.users.total} color="#11783A" />
            <SplitCard label="Pros / Agences" value={data.users.pro} total={data.users.total} color="#D6202C" />
          </div>
        </Section>

        {/* Listings by country */}
        <Section title="Annonces par pays">
          <div className="grid grid-cols-3 gap-4">
            {Object.entries(data.listings.by_country).map(([c, n]) => (
              <div key={c} className="bg-white border border-[#E5E5E5] rounded-2xl p-5">
                <div className="text-2xl mb-1">{COUNTRY_FLAGS[c]}</div>
                <div className="font-heading text-3xl font-semibold">{n}</div>
                <div className="text-xs text-muted-foreground">{COUNTRY_LABELS[c]}</div>
              </div>
            ))}
          </div>
        </Section>

        {/* Top cities */}
        <Section title="Top 10 villes">
          <div className="bg-white border border-[#E5E5E5] rounded-2xl divide-y divide-[#F0EBE6]">
            {data.listings.top_cities.map((c, i) => (
              <div key={`${c.city}-${i}`} className="p-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="w-7 h-7 rounded-full bg-[#FEF2F3] text-[#D6202C] text-xs flex items-center justify-center font-medium">{i + 1}</span>
                  <span className="font-medium">{c.city}</span>
                  <span className="text-xs text-muted-foreground">{COUNTRY_FLAGS[c.country]}</span>
                </div>
                <span className="text-sm tabular-nums font-medium">{c.count}</span>
              </div>
            ))}
            {data.listings.top_cities.length === 0 && <div className="p-8 text-center text-muted-foreground text-sm">Aucune donnée</div>}
          </div>
        </Section>

        {/* Property types */}
        <Section title="Types de biens">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {data.listings.by_property_type.map((p) => (
              <div key={p.type} className="bg-white border border-[#E5E5E5] rounded-2xl p-4">
                <div className="font-heading text-2xl font-semibold">{p.count}</div>
                <div className="text-xs text-muted-foreground capitalize">{p.type}</div>
              </div>
            ))}
          </div>
        </Section>

        {/* Engagement */}
        <Section title="Engagement">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <KpiCard icon={<MessageSquare />} label="Messages totaux" value={data.engagement.total_messages} sub={`${data.engagement.period_messages} sur la période`} />
            <KpiCard icon={<Heart />} label="Favoris" value={data.engagement.total_favorites} />
            <KpiCard icon={<ShieldCheck />} label="KYC validés" value={data.kyc.approved} sub={`${data.kyc.pending} en attente`} />
          </div>
        </Section>
      </div>
    </Layout>
  );
}

const Section = ({ title, children, ...rest }) => (
  <div className="mb-8" {...rest}>
    <h2 className="font-heading text-xl font-medium mb-3">{title}</h2>
    {children}
  </div>
);

const KpiCard = ({ icon, label, value, sub, highlight }) => (
  <div className={`rounded-2xl p-5 border ${highlight ? "bg-gradient-to-br from-[#F4B400] to-[#D6202C] text-white border-transparent" : "bg-white border-[#E5E5E5]"}`}>
    <div className={`w-9 h-9 rounded-xl flex items-center justify-center mb-3 ${highlight ? "bg-white/20" : "bg-[#FEF2F3] text-[#D6202C]"}`}>
      {icon}
    </div>
    <div className="font-heading text-3xl font-semibold leading-none">{value}</div>
    <div className="text-xs mt-1 opacity-90">{label}</div>
    {sub && <div className={`text-[11px] mt-2 ${highlight ? "text-white/80" : "text-muted-foreground"}`}>{sub}</div>}
  </div>
);

const SplitCard = ({ label, value, total, color }) => {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div className="bg-white border border-[#E5E5E5] rounded-2xl p-5">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm">{label}</span>
        <span className="font-heading text-2xl font-semibold" style={{ color }}>{value}</span>
      </div>
      <div className="h-2 bg-[#F5F0EB] rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: color }} />
      </div>
      <div className="text-xs text-muted-foreground mt-1.5">{pct}%</div>
    </div>
  );
};

const RevenueCard = ({ label, data, count, highlight }) => {
  const entries = Object.entries(data || {});
  return (
    <div className={`rounded-2xl p-5 border ${highlight ? "border-[#11783A]/30 bg-[#11783A]/5" : "border-[#E5E5E5] bg-white"}`}>
      <div className="text-xs text-muted-foreground mb-3">{label} · {count} transaction(s)</div>
      {entries.length === 0 ? (
        <div className="text-sm text-muted-foreground">—</div>
      ) : (
        <div className="space-y-1">
          {entries.map(([cur, amount]) => (
            <div key={cur} className="flex items-center justify-between">
              <span className="text-xs uppercase tracking-wider text-muted-foreground">{cur}</span>
              <span className="font-heading text-2xl font-semibold tabular-nums">{new Intl.NumberFormat("fr-FR").format(amount)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
